import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowRight, Upload, Mail, Wrench, MapPin, Truck, Map } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navbar, Footer } from "@/components/Layout";
import { ListingCard } from "@/components/ListingCard";
import type { Listing } from "@/lib/mockData";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useSeo } from "@/hooks/useSeo";

type ListingRow = {
  id: string;
  title: string;
  price: number;
  price_unit: string | null;
  category: string;
  species: string;
  images: string[] | null;
  location: string | null;
  delivery: string;
  description: string | null;
};

function useRandomListings(count: number) {
  return useQuery({
    queryKey: ["landing-listings", count],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listings")
        .select("id, title, price, price_unit, category, species, images, location, delivery, description")
        .eq("status", "aktiv")
        .limit(20);
      if (error) throw error;
      if (!data || data.length === 0) return [];
      const shuffled = [...data].sort(() => Math.random() - 0.5);
      return shuffled.slice(0, count) as ListingRow[];
    },
    staleTime: 60_000,
  });
}

function toListingCardFormat(l: ListingRow) {
  return {
    id: l.id,
    title: l.title,
    price: l.price,
    priceUnit: l.price_unit ?? "",
    category: l.category as Listing["category"],
    species: l.species as Listing["species"],
    images: l.images ?? [],
    location: l.location ?? "",
    delivery: l.delivery as Listing["delivery"],
    description: l.description ?? "",
    sellerName: "",
    status: "aktiv" as const,
  };
}

const ways = [
  {
    icon: Upload,
    title: "Ladda upp en annons",
    desc: "Har du ved, timmer eller plankor att sälja? Skapa en annons på några minuter och nå köpare i hela Sverige.",
    href: "/skapa-annons",
  },
  {
    icon: Mail,
    title: "Ställ din fråga",
    desc: "Har du funderingar eller behöver hjälp? Hör av dig till oss så svarar vi så snart vi kan.",
    href: "mailto:info@timbernet.se",
  },
  {
    icon: Wrench,
    title: "Utforska våra verktyg",
    desc: "TimberNet erbjuder smarta verktyg som areakalkylator för att göra din virkesaffär enklare och mer informerad.",
    href: "/skogsverktyg",
  },
];

function PlaceholderCard() {
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-card">
      <div className="flex items-center justify-center bg-secondary" style={{ aspectRatio: "4/3" }}>
        <span className="text-4xl">🌲</span>
      </div>
      <div className="p-4 space-y-2">
        <div className="h-4 w-3/4 rounded bg-muted animate-pulse" />
        <div className="h-3 w-1/2 rounded bg-muted animate-pulse" />
        <div className="h-3 w-1/3 rounded bg-muted animate-pulse" />
      </div>
    </div>
  );
}

export default function Index() {
  useSeo({
    title: "TimberNet — Marknadsplats för virke och skogstjänster",
    description: "Köp och sälj virke, ved och skogstjänster direkt mellan privatpersoner och företag. Sveriges digitala marknadsplats för skog.",
    path: "/",
    appendSiteName: false,
  });
  const heroListings = useRandomListings(2);
  const popularListings = useRandomListings(4);

  useEffect(() => {
    const first = heroListings.data?.[0]?.images?.[0];
    if (!first) return;
    const link = document.getElementById("lcp-preload") as HTMLLinkElement | null;
    if (link && link.href !== first) {
      link.href = first;
    }
  }, [heroListings.data]);

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />

      {/* Section 1 – Hero */}
      <section className="bg-background">
        <div className="container py-16 md:py-24">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div className="max-w-lg">
              <h1 className="mb-4 text-3xl font-bold leading-tight text-foreground md:text-4xl lg:text-5xl">
                Sveriges marknadsplats för virke och skog
              </h1>
              <p className="mb-8 text-base text-muted-foreground md:text-lg">
                Köp och sälj ved, timmer, plankor och mycket mer – direkt mellan privatpersoner och företag.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button size="lg" asChild>
                  <Link to="/annonser" state={{ fresh: true }}>Gå till marknadsplats</Link>
                </Button>
                <Button
                  size="lg"
                  variant="ghost"
                  className="bg-transparent text-muted-foreground hover:bg-secondary hover:text-foreground border border-forest border-solid"
                  asChild
                >
                  <Link to="/registrera">Registrera dig på 1 minut</Link>
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <h2 className="sr-only">Utvalda annonser från marknadsplatsen</h2>
              {heroListings.data && heroListings.data.length >= 2
                ? heroListings.data.map((l, i) => (
                    <div key={l.id} className="h-full rounded-lg overflow-hidden shadow-card-hover [&>a]:h-full">
                      <ListingCard listing={toListingCardFormat(l)} priority={i < 2} />
                    </div>
                  ))
                : Array.from({ length: 2 }).map((_, i) => <PlaceholderCard key={i} />)}
            </div>
          </div>
        </div>
      </section>

      {/* Section 2 – Populära annonser */}
      <section className="py-16">
        <div className="container">
          <div className="mb-8 text-center">
            <h2 className="text-2xl font-bold md:text-3xl">Populära annonser</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Utforska några av våra senaste annonser på marknadsplatsen.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4">
            {popularListings.data && popularListings.data.length > 0
              ? popularListings.data.map((l) => (
                  <ListingCard key={l.id} listing={toListingCardFormat(l)} />
                ))
              : Array.from({ length: 4 }).map((_, i) => <PlaceholderCard key={i} />)}
          </div>
          <div className="mt-8 text-center">
            <Button variant="outline" asChild>
              <Link to="/annonser" state={{ fresh: true }}>
                Se alla annonser <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Section 3 – Beräkna din yta */}
      <section className="bg-gradient-forest text-primary-foreground">
        <div className="container py-16 md:py-24">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div className="max-w-lg">
              <h2 className="mb-4 text-2xl font-bold text-primary-foreground md:text-3xl">Beräkna din yta</h2>
              <p className="mb-6 text-sm text-primary-foreground/80 md:text-base leading-relaxed">
                Med TimberNets inbyggda areakalkylator kan du enkelt rita ut ett markområde direkt på kartan.
                Placera ut punkter längs gränserna av ditt område – punkterna kopplas ihop och arean
                beräknas automatiskt i både kvadratmeter och hektar. Perfekt för dig som ska sälja virke
                och vill ge köparen en tydlig bild av arealen.
              </p>
              <Button size="lg" variant="secondary" asChild>
                <Link to="/skogsverktyg/arearaknare">Prova areakalkylator</Link>
              </Button>
            </div>
            <div className="flex items-center justify-center">
              <div className="flex h-40 w-40 items-center justify-center rounded-2xl bg-primary-foreground/10 md:h-56 md:w-56">
                <Map className="h-20 w-20 text-primary-foreground/60 md:h-28 md:w-28" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4 – Börja på andra sätt */}
      <section className="py-16">
        <div className="container">
          <div className="mb-8 text-center">
            <h2 className="text-2xl font-bold md:text-3xl">Börja på andra sätt!</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Det finns flera sätt att komma igång på TimberNet.
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {ways.map((w) => {
              const isExternal = w.href.startsWith("mailto");
              const className = "flex flex-col items-center rounded-lg bg-card p-6 text-center shadow-card hover:shadow-card-hover transition-shadow cursor-pointer no-underline";
              return isExternal ? (
                <a key={w.title} href={w.href} className={className}>
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10">
                    <w.icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="mb-2 text-base font-semibold">{w.title}</h3>
                  <p className="text-sm text-muted-foreground">{w.desc}</p>
                </a>
              ) : (
                <Link key={w.title} to={w.href} className={className}>
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10">
                    <w.icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="mb-2 text-base font-semibold">{w.title}</h3>
                  <p className="text-sm text-muted-foreground">{w.desc}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
